import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { getAuth } from "firebase/auth";

const LoadGameScreen = ({ onBack, onSelectGame, onHome, onLogout }) => {
    const [inProgressGames, setInProgressGames] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // Stores the game to delete
    const appId = "1:699575750232:web:8d7ebbfd7a7bb5c866842d";

    useEffect(() => {
        fetchGames();
    }, []);

    const fetchGames = async () => {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
            const db = getFirestore();
            const gamesCollection = collection(db, "personalData", appId, "users", user.uid, "games");
            const gamesSnapshot = await getDocs(gamesCollection);
            const gamesData = gamesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filter only in-progress games (marked as playing or not finished)
            // Exclude games that have 13 hands (finished) even if marked as playing
            const inProgress = gamesData.filter(game =>
                (game.gameState === 'playing' || (!game.winner && !game.winners)) &&
                (!game.handHistory || game.handHistory.length < 13)
            );
            setInProgressGames(inProgress.sort((a, b) => new Date(b.date) - new Date(a.date)));
        }
    };

    const handleDeleteClick = (e, game) => {
        e.stopPropagation();
        setDeleteConfirm(game);
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;

        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const db = getFirestore();
                await deleteDoc(doc(db, "personalData", appId, "users", user.uid, "games", deleteConfirm.id));
                setDeleteConfirm(null);
                fetchGames(); // Refresh list
            }
        } catch (error) {
            console.error("Error deleting game:", error);
        }
    };

    return (
        <div className="h-screen bg-[#1a2332] text-white font-['Inter'] flex flex-col items-center relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 C 35 15 45 15 45 25 C 45 35 30 50 30 50 C 30 50 15 35 15 25 C 15 15 25 15 30 5 Z' fill='%23ffffff' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }}
                />
            </div>

            {/* Ambient Glow */}
            <div className="absolute top-0 left-1/4 w-[50vw] h-[50vw] bg-purple-500/10 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[50vw] h-[50vw] bg-blue-500/10 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

            <header className="relative z-20 flex items-center justify-between w-full max-w-4xl p-4 sm:p-6">
                <button
                    onClick={onBack}
                    className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                    title="Back"
                >
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight font-['Roboto_Slab'] text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400">
                    RESUME GAME
                </h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onHome}
                        className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                        title="Home"
                    >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>
                    </button>
                    <button
                        onClick={onLogout}
                        className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                        title="Logout"
                    >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    </button>
                </div>
            </header>

            <div className="relative z-10 w-full max-w-4xl flex-1 overflow-hidden px-4 pb-6">
                <div className="h-full bg-[#1e293b]/50 backdrop-blur-md rounded-3xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
                    {inProgressGames.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
                            <span className="text-6xl mb-4">⏸️</span>
                            <p className="text-xl font-bold text-gray-300 mb-2">No Paused Games</p>
                            <p className="text-sm text-gray-500">Start a new game or pause one to resume later!</p>
                        </div>
                    ) : (
                        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-12 sm:pb-12 space-y-4">
                            {inProgressGames.map((game, index) => {
                                const handsPlayed = game.handHistory?.length || 0;
                                const progress = Math.round((handsPlayed / 13) * 100);

                                return (
                                    <button
                                        key={index}
                                        onClick={() => onSelectGame(game)}
                                        className="w-full text-left bg-black/20 rounded-2xl border border-white/5 p-4 hover:bg-white/5 hover:border-amber-500/30 transition-all duration-200 group"
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <h2 className="text-base font-bold text-white group-hover:text-amber-400 transition-colors">
                                                    {new Date(game.date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                                                </h2>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {new Date(game.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="text-right">
                                                    <p className="text-xs text-gray-400 uppercase tracking-wider">Progress</p>
                                                    <p className="text-lg font-bold text-amber-400">{handsPlayed}/13</p>
                                                </div>
                                                <div className="flex items-center text-gray-500 group-hover:text-amber-400 transition-colors bg-white/5 px-3 py-1 rounded-lg border border-white/10 mr-2">
                                                    <span className="text-xs font-bold uppercase tracking-wider mr-2">Resume</span>
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <button
                                                    onClick={(e) => handleDeleteClick(e, game)}
                                                    className="p-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 transition-all"
                                                    title="Delete Game"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="w-full bg-black/30 rounded-full h-2 mb-3 overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-300"
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>

                                        {/* Players */}
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-xs text-gray-400">Players:</span>
                                            {game.players?.map((player, pIndex) => (
                                                <span key={pIndex} className="text-xs font-bold px-2 py-1 rounded-full bg-white/5" style={{ color: player.color }}>
                                                    {player.name}
                                                </span>
                                            ))}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>


            {/* Delete Confirmation Modal */}
            {
                deleteConfirm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <div className="w-full max-w-sm bg-[#1e293b] rounded-2xl border border-white/10 shadow-2xl p-6">
                            <h3 className="text-xl font-bold text-white mb-2">Delete Game?</h3>
                            <p className="text-gray-400 mb-6">
                                Are you sure you want to delete the game from {new Date(deleteConfirm.date).toLocaleDateString()}? This action cannot be undone.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 font-bold hover:bg-white/10 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="flex-1 px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 font-bold hover:bg-red-500/30 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default LoadGameScreen;
